<template>
    <div class="chart-container" v-if="label">
        <canvas id="chart"></canvas>
    </div>
</template>

<script>
import Chart from 'chart.js';
import pattern from 'patternomaly';
import moment from 'moment';
import 'hammerjs';
import 'chartjs-plugin-zoom';
// import * as ChartAnnotation from 'chartjs-plugin-annotation';
import { mapState, mapActions } from "vuex";
// import { getSCEFriendlyName } from '../helpers/constants'
import { getRegisterFriendlyName } from '../helpers';

// Draws the "Consommation globale" legend entry as a line with a centered dot instead of
// the default filled rectangle,
const globalConsumptionLegendMarkerPlugin = {
    id: 'globalConsumptionLegendMarker',
    afterDraw(chartInstance) {
        const legend = chartInstance.legend;
        if (!legend || !legend.legendItems || !legend.legendHitBoxes) return;

        const boxWidth = (legend.options && legend.options.labels && legend.options.labels.boxWidth) || 40;
        const ctx = chartInstance.ctx;

        legend.legendItems.forEach((item, i) => {
            const dataset = chartInstance.data.datasets[item.datasetIndex];
            const hitBox = legend.legendHitBoxes[i];
            if (!dataset || !dataset._isGlobalConsumptionLine || !hitBox) return;

            const midY = hitBox.top + hitBox.height / 2;
            const color = dataset.borderColor || '#000';

            ctx.save();
            ctx.strokeStyle = color;
            ctx.fillStyle = color;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(hitBox.left, midY);
            ctx.lineTo(hitBox.left + boxWidth, midY);
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(hitBox.left + boxWidth / 2, midY, 3, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        });
    }
};

export default {
    // 'yearsToCompare', 'fromMonth', 'fromDay', 'toMonth', 'toDay' are only needed if "compareByYear" is true
    // 'allDate' is only used if "compareByYear" is false
    props: [ 'registersData', 'allDates', 'aggregation', 'compareByYear', 'yearsToCompare',
        'fromMonth', 'fromDay', 'toMonth', 'toDay', 'overallConsumptions', 'selectedTimeframes',
        // Register IDs were previously used to map stable colors.
        'podInputUnit', 'conversionToKWH', 'dataIsInKWH',
        'currentUnit', 'viewMultiplePods', 'comparePods',
        // grouped incomplete periods : array of incomplete periods that are grouped either by pod
        // or by register (depending on 'viewMultiplePods' value)
        'groupedIncompletePeriods', 'eventsWithIndex',
        'showBenchmarking', 'benchmarkingCurve', 'accessibilityEnabled'],

    data() {
        return {
            chart: null,
            //colors: null,
            //registersColors: [],
            showingOverallConsumption: false,
            // Rapid interactions on the chart (slider) security
            isCreatingChart: false,
            chartTimeout: null,
            isMounted: false,
        }
    },
    created() {
        this.getLabel({ view: 'data' })
        /*
        // Generate patterns and colors
        const colors = [
          ["rgb(255, 99, 132)", "rgba(255, 99, 132, 0.8)", "rgba(255, 99, 132, 0.6)", "rgba(255, 99, 132, 0.4)", "rgba(255, 99, 132, 0.2)"], // 1
          ["rgb(54, 162, 235)", "rgba(54, 162, 235, 0.8)", "rgba(54, 162, 235, 0.6)", "rgba(54, 162, 235, 0.4)", "rgba(54, 162, 235, 0.2)"], // 6
          ["rgb(255, 159, 64)", "rgba(255, 159, 64, 0.8)", "rgba(255, 159, 64, 0.6)", "rgba(255, 159, 64, 0.4)", "rgba(255, 159, 64, 0.2)"], // 2
          ["rgb(103, 212, 118)", "rgba(103, 212, 118, 0.8)", "rgba(103, 212, 118, 0.6)", "rgba(103, 212, 118, 0.4)", "rgba(103, 212, 118, 0.2)"], // 4
          ["rgb(255, 205, 86)", "rgba(255, 205, 86, 0.8)", "rgba(255, 205, 86, 0.6)", "rgba(255, 205, 86, 0.4)", "rgba(255, 205, 86, 0.2)"], // 3
          ["rgb(75, 192, 192)", "rgba(75, 192, 192, 0.8)", "rgba(75, 192, 192, 0.6)", "rgba(75, 192, 192, 0.4)", "rgba(75, 192, 192, 0.2)"], // 5
          ["rgb(153, 102, 255)", "rgba(153, 102, 255, 0.8)", "rgba(153, 102, 255, 0.6)", "rgba(153, 102, 255, 0.4)", "rgba(153, 102, 255, 0.2)"], // 8
          ["rgb(61, 78, 224)", "rgba(61, 78, 224, 0.8)", "rgba(61, 78, 224, 0.6)", "rgba(61, 78, 224, 0.4)", "rgba(61, 78, 224, 0.2)"], // 7
          ["rgb(190, 100, 212)", "rgba(190, 100, 212, 0.8)", "rgba(190, 100, 212, 0.6)", "rgba(190, 100, 212, 0.4)", "rgba(190, 100, 212, 0.2)"], // 9
          // ["rgb(201, 203, 207)", "rgba(201, 203, 207, 0.8)", "rgba(201, 203, 207, 0.6)", "rgba(201, 203, 207, 0.4)", "rgba(201, 203, 207, 0.2)"], // 10
        ]
        
        // If accessibilityEnabled is true, use the patterns, or use default colors instead
        if (this.accessibilityEnabled) {
          const patternList = ['plus', 'cross', 'dash', 'cross-dash', 'dot-dash', 'dot', 'circle', 'ring', 'line']
          const patterns = colors.map((colorGroup, index) => {
            return colorGroup.map(color => {
              return pattern.draw(patternList[index], color, undefined, 16)
            })
          })
          this.colors = patterns
        } else {
          this.colors = colors
        }

        let iColor = 0;
        for(let i in this.allRegistersIDs) {
            this.registersColors[this.allRegistersIDs[i]] = this.colors[iColor % this.colors.length];
            iColor++;
        }*/
    },
    mounted() {
        this.isMounted = true;
        this.displayChart();
    },
    computed: {
         // max and min are used to set height limits to box annotations
        // maxInjection() {
        //     let max = 0;
        //     this.consumptions.forEach(cons => {

        //     });
        //     return null;
        // },
        // maxConsumption() {

        // }
        ...mapState({ account: state => state.account }),
        ...mapState({ label: state => state.labels.data }),
        userLanguage() {
            return this.account.user.lang;
        },
        // hasLeapYear() {
        //     let hasLeapYear = false;
        //         for(let i = 0; i < 3; i++) {
        //             if(moment(this.yearsToCompare[i], "YYYY").isLeapYear()) {
        //                 hasLeapYear = true;
        //                 break;
        //             }
        //         }
        //     return hasLeapYear;
        // },
        // indexOfFeb29th() {
        //     const febThe29th = moment(`2020-02-29`, "YYYY-MM-DD");
        //     const startDate = moment(`2020-${this.fromMonth}-${this.fromDay}`, "YYYY-MMMM-DD");
        //     const endDate = moment(`2020-${this.toMonth}-${this.toDay}`, "YYYY-MMMM-DD");
        //     if(startDate <= febThe29th && endDate >= febThe29th) {
        //         let indexOfFebThe29th = 0;
        //         let date = moment(startDate);
        //         while(date < febThe29th) {
        //             indexOfFebThe29th++;
        //             date = date.add(1, 'day');
        //         }
        //         return indexOfFebThe29th;
        //     }
        //     return -1;
        // },
        // showingHighAggregation() {
        //     return this.aggregation == 'QUARTER_H' || this.aggregation == 'HOUR'
        // }
    },
    methods: {
        /*testChart() {
            let dataset = new Array();
            dataset.data = new Array();
            // put same dataset but with opposite value to simulate the "injection" part of the graphic
            let testDataSet = new Array();
            testDataSet.data = new Array();

            let labels = new Array();

            // let order = 0;
            this.consumptions.forEach(consumption => {
                dataset.data.push(consumption.value);
                testDataSet.data.push(-consumption.value);
                let label;
                switch(this.aggregation.toLowerCase()) {
                case "day":
                    label = moment(new Date(consumption.startDate)).format("DD-MM-YYYY");
                    break;
                case "week":
                    label = "Semaine du " + moment(new Date(consumption.startDate)).format("DD-MM-YYYY");
                    break;
                }
                labels.push(label);
            });

            const options = {
                tooltips: {
                    mode: 'index',
                    intersect: false
                },
                responsive: true,
                scales: {
                    yAxes: [{
                        stacked: true,
                        id: 'y-axis-0',
                    }],
                    xAxes: [{
                        gridLines: {
                            display: false
                        },
                        stacked: true,
                        id: 'x-axis-0',
                        ticks: {
                            autoSkip: true,
                            autoSkipPadding: 5,
                            maxRotation: 90,
                            padding: 10
                        }
                    }]
                },
                annotation: {
                    annotations: [
                        {
                            type: 'box',
                            drawTime: 'beforeDatasetsDraw',
                            // ID of the X scale to bind onto
                            xScaleID: 'x-axis-0',
                            // ID of the Y scale to bind onto
                            yScaleID: 'y-axis-0',
                            // Stroke color
                            borderColor: 'rgba(0, 0, 0, 0.21)',
                            // Stroke width
                            borderWidth: 1,
                            // Fill color
                            backgroundColor: 'rgba(0, 0, 0, 0.21)',
                            xMin: labels[1],
                            xMax: labels[3],
                            yMin: -4,
                            yMax: 4
                        },
                        {
                            type: 'box',
                            drawTime: 'beforeDatasetsDraw',
                            // ID of the X scale to bind onto
                            xScaleID: 'x-axis-0',
                            // ID of the Y scale to bind onto
                            yScaleID: 'y-axis-0',
                            // Stroke color
                            borderColor: 'rgba(0, 0, 0, 0.21)',
                            // Stroke width
                            borderWidth: 2,
                            // Fill color
                            backgroundColor: 'rgba(0, 0, 0, 0.21)',
                            xMin: labels[6],
                            xMax: labels[8],
                            yMin: -4,
                            yMax: 4
                        }
                    ]
                }
            }

            let datasets = [
                {
                    label: "Prélèvement calculé",
                    backgroundColor: "#163BE1",
                    data: dataset.data
                },
                {
                    label: "Injection calculée",
                    backgroundColor: "#EA6F55",
                    data: testDataSet.data,
                }
            ];

            if(!this.ctx)
                this.ctx = document.getElementById('chart');
            if(this.chart) {
                this.chart.destroy();
            }
            this.chart = new Chart(this.ctx.getContext('2d'), {
                plugins: [ChartAnnotation],
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: datasets
                },
                options: options
            });
            this.chart.render();
        },
        */
       ...mapActions("labels", ["getLabel"]),
        // isLeapYear(year) {
        //     return moment(year, "YYYY").isLeapYear();
        // },
        displayChart() {
          const trad = {
            estimatedCost: this.label.texts.estimatedCost,
            consumption: this.label.texts.consumption,
            globalConsumption: this.label.texts.globalConsumption,
            averageUserConsumption: this.label.texts.averageUserConsumption
          }
            let props = this
            let labels = this.generateXAxisLabels();
            let minLimit = null;
            let maxLimit = null;

            if (props.aggregation === 'QUARTER_H' && labels.length > 0) {
                minLimit = moment(labels[0]).startOf('day').valueOf();
                maxLimit = moment(minLimit).add(1, 'days').valueOf();
            }

            const options = {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    yAxes: [{
                        stacked: !this.showBenchmarking,
                        id: 'y-axis-0',
                        scaleLabel: {
                            display: true,
                            labelString: this.currentUnit
                        }
                    }],
                    xAxes: [{
                        type: (props.aggregation === 'QUARTER_H') ? 'time' : 'category',
                        stacked: true,
                        id: 'x-axis-0',
                        time: (props.aggregation === 'QUARTER_H') ? {
                            unit: props.aggregation === 'QUARTER_H' ? 'minute' : 'hour',
                            stepSize: props.aggregation === 'QUARTER_H' ? 15 : 1,
                            tooltipFormat: 'DD/MM HH[h]mm',
                            displayFormats: {
                                minute: 'DD/MM HH[h]mm',
                                hour: 'DD/MM HH[h]mm',
                                day: 'DD/MM HH[h]mm'
                            }
                        } : undefined,
                        ticks: {
                            source: props.aggregation === 'QUARTER_H' ? 'labels' : 'auto',
                            autoSkip: true,
                            autoSkipPadding: 5,
                            maxRotation: 90,
                            padding: 15,
                            min: (props.aggregation === 'QUARTER_H') ? minLimit : undefined,
                            max: (props.aggregation === 'QUARTER_H') ? maxLimit : undefined
                        },
                        gridLines: {
                            display: (props.aggregation === 'QUARTER_H'), 
                            offsetGridLines: props.aggregation !== 'QUARTER_H'
                        }
                    }]
                },
                legend: {
                    display: true,
                    position: 'bottom',
                    align: 'start',
                    labels: {
                        generateLabels: function(chartInstance) {
                            const items = Chart.defaults.global.legend.labels.generateLabels(chartInstance);
                            items.forEach(item => {
                                const dataset = chartInstance.data.datasets[item.datasetIndex];
                                if (dataset && dataset._isGlobalConsumptionLine) {
                                    // Hide the default filled box for Global Consumption; globalConsumptionLegendMarkerPlugin
                                    // draws a line + dot marker in its place instead
                                    item.fillStyle = 'rgba(0, 0, 0, 0)';
                                    item.strokeStyle = 'rgba(0, 0, 0, 0)';
                                    item.lineWidth = 0;
                                }
                            });
                            return items;
                        }
                    }
                },
                // zoom Configuration
                plugins: {
                    zoom: {
                        zoom: {
                            enabled: props.aggregation === 'QUARTER_H',
                            mode: 'x',
                            speed: 0.2,
                            drag: false,
                            rangeMin: { x: minLimit },
                            rangeMax: { x: maxLimit }
                        },
                        pan: {
                            enabled: props.aggregation === 'QUARTER_H',
                            mode: 'x',
                            rangeMin: { x: minLimit },
                            rangeMax: { x: maxLimit }
                        }
                    }
                },
                // events
                hover: {
                    mode : 'index',
                    intersect : false,
                  animationDuration: 0
                },
                animation: {
                  duration: 1,
                  onComplete: function() {
                    const chartInstance = this.chart;
                    const ctx = chartInstance && chartInstance.ctx;
                    // Don't draw chart if the component is not mounted or if the chart is still being created
                    if(!ctx || props.isCreatingChart !== false) { return }

                    const meta = chartInstance.controller.getDatasetMeta(0);
                    const FONT_SIZE = 22

                    if (props.eventsWithIndex && props.eventsWithIndex.length > 0) {
                      ctx.font = Chart.helpers.fontString(FONT_SIZE, Chart.defaults.global.defaultFontStyle, Chart.defaults.global.defaultFontFamily);
                      ctx.textAlign = 'center';
                      ctx.textBaseline = 'bottom';
                      ctx.fillStyle = 'rgb(34, 83, 148)';

                      props.eventsWithIndex.forEach(event => {
                        if(meta.data[event.index])
                            ctx.fillText("🅔", meta.data[event.index]._model.x, meta.data[event.index]._yScale.bottom + FONT_SIZE + 3)
                      })
                    }
                  }
                },
                // callback to generate tooltips
                tooltips: {
                    mode: 'index',
                    intersect: false,
                    callbacks: {
                        title: function(tooltipItems, data) {
                            if (props.aggregation === 'QUARTER_H') {
                                const startLabel = moment(data.labels[tooltipItems[0].index]).format('DD/MM HH[h]mm');
                                const endLabel = moment(data.labels[tooltipItems[0].index]).add(15, 'minutes').format('HH[h]mm');
                                return `${startLabel} - ${endLabel}`;
                            }
                            return data.labels[tooltipItems[0].index];
                        },
                        label: function(tooltipItem, data) {
                            let conversionValue = props.dataIsInKWH ? 1/props.conversionToKWH : props.conversionToKWH
                            const precision = (props.aggregation === 'QUARTER_H' || props.aggregation === 'HOUR') ? 3 : 3
                            
                            let rawValue = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]

                            if (typeof rawValue === 'object' && rawValue !== null) {
                                if (rawValue._isNullData) {
                                    return '';
                                }
                                if (rawValue._isZeroData) {
                                    rawValue = 0;
                                } 
                                else if (rawValue.y !== undefined) {
                                    rawValue = rawValue.y;
                                }
                            }
                            if(rawValue === null || rawValue === undefined || Number.isNaN(Number(rawValue))) {
                                return ''
                            }
                            // value
                            let label = "" + Number(rawValue).toFixed(precision)
                            // unit
                            label = label.concat(props.dataIsInKWH ? 'kWh' : data.datasets[tooltipItem.datasetIndex].inputUnit);
                            if(props.conversionToKWH !== 1) {
                                const valueInOtherUnit = Number(rawValue) * conversionValue
                                // value
                                label = label.concat("  (" + ( valueInOtherUnit.toFixed(precision)) + " ");
                                // unit
                                label = label.concat((props.dataIsInKWH ? data.datasets[tooltipItem.datasetIndex].inputUnit : 'kWh') + ')');
                            }
                            // if(data.datasets[tooltipItem.datasetIndex].costPerPeriod) {
                            //     label = label.concat(`\n${trad.estimatedCost} : ${Number(data.datasets[tooltipItem.datasetIndex].costPerPeriod[tooltipItem.index]).toFixed(2)} €`)
                            // }
                            return label
                        },
                        footer: function(tooltipItems, data) {
                          let result = [];
                          
                          // Add events if any
                          if (props.eventsWithIndex) {
                            const eventIndex = props.eventsWithIndex.filter(event => tooltipItems[0].index == event.index)
                            eventIndex.forEach(event => {
                              const dateText = moment(event.event.date).format('DD-MM-YYYY');
                              const type = event.event.type ? ` (${event.event.type})` : "";
                              const description = event.event.description ? ` : ${event.event.description}` : ""
                              result.push(`${dateText} - ${event.event.category}${type}${description}`);
                            })
                          }
                          
                          // For QUARTER_H: show "Données non disponibles" once if ALL datasets are NULL
                          if (props.aggregation === 'QUARTER_H' && tooltipItems[0]) {
                            const dataIndex = tooltipItems[0].index;
                            const allDatasetsNull = data.datasets.every(dataset => {
                              const dataPoint = dataset.data[dataIndex];
                              return dataPoint && dataPoint._isNullData;
                            });
                            
                            if (allDatasetsNull) {
                              result.push('Données non disponibles');
                            }
                          }
                          
                          return result.length > 0 ? result : undefined;
                        }
                    }
                },
                /*
                onClick: function(event) {
                    let elementDetails = this.chart.getElementAtEvent(event)
                    if(elementDetails.length == 0)
                        return
                    elementDetails = elementDetails[0]
                    // on a multi-POD view, the most detailed view is DAY, so return in this case
                    if(props.viewMultiplePods && props.aggregation == 'DAY') {
                        return
                    }
                    if(props.showingOverallConsumption && elementDetails._datasetIndex == 0 || props.compareByYear || props.aggregation == 'QUARTER_H' || props.showBenchmarking) {
                        // the overall consumption has been clicked, so nothing has to be done
                        return
                    }
                    // emit an event to the "MyData" vue component so it details the clicked bar
                    props.handleBarClick(elementDetails._datasetIndex, elementDetails._index)
                }
                */
                onClick: function(event) {
                    let elements = props.chart.getElementsAtEventForMode(event, 'index', { intersect: false });
                    
                    if (!elements || elements.length === 0) {
                        elements = props.chart.getElementsAtXAxis(event);
                    }

                    if (!elements || elements.length === 0) {
                        elements = props.chart.getElementAtEvent(event);
                    }

                    if (!elements || elements.length === 0) return;

                    let elementDetails = elements[0];

                    if (props.viewMultiplePods && props.aggregation == 'DAY') return;
                    
                    if (props.compareByYear || props.aggregation == 'QUARTER_H' || props.showBenchmarking) return;

                    props.handleBarClick(elementDetails._datasetIndex, elementDetails._index);
                }
            }
            let datasets = []
            if(this.compareByYear) {
                // if the periods to compare contain the 29th of february and
                // aggregation = day and one of the years to compare is a leap year
                // then add a zero value at the 29th of february to each non-leap year
                // const needToAdd29thOfFeb = this.aggregation == "DAY" && this.hasLeapYear && this.indexOfFeb29th != -1;
                // for(let yearI in this.registersData) {
                //     for(let registerI in this.registersData[yearI].registers) {
                //         // if necessary, add a zero value right after february the 28th
                //         if(needToAdd29thOfFeb && !this.isLeapYear(this.yearsToCompare[yearI])) {
                //             if(this.registersData[yearI].registers[registerI].injections.estimatedData)
                //                 this.registersData[yearI].registers[registerI].injections.estimatedData.splice(this.indexOfFeb29th, 0, 0);

                //             if(this.registersData[yearI].registers[registerI].injections.measuredData)
                //                 this.registersData[yearI].registers[registerI].injections.measuredData.splice(this.indexOfFeb29th, 0, 0);

                //             if(this.registersData[yearI].registers[registerI].withdrawals.estimatedData)
                //                 this.registersData[yearI].registers[registerI].withdrawals.estimatedData.splice(this.indexOfFeb29th, 0, 0);

                //             if(this.registersData[yearI].registers[registerI].withdrawals.measuredData)
                //                 this.registersData[yearI].registers[registerI].withdrawals.measuredData.splice(this.indexOfFeb29th, 0, 0);
                //         }
                //         let generatedDatasets = this.generateDatasetObjects(this.registersData[yearI].registers[registerI], this.registersData[yearI].registers[registerI].variableID, yearI, this.yearsToCompare[yearI]);//here
                //         // console.log(generatedDatasets)
                //         generatedDatasets.forEach(ds => datasets.push(ds));
                //     }
                // }
                for(let yearI in this.registersData) {
                    let generatedDatasets = this.generateDatasetObjects(this.registersData[yearI].overallConsumptions, yearI, this.yearsToCompare[yearI])
                    generatedDatasets.forEach(ds => datasets.push(ds))
                }
            }
            else {
                this.showingOverallConsumption = false
                if(this.overallConsumptions && this.aggregation != "QUARTER_H" && this.aggregation != "HOUR" && !(this.comparePods && this.viewMultiplePods)) {
                    // The parent component precomputes the correct global total.
                    let globalData = this.overallConsumptions;
                    // Ensure globalData length matches labels length so the line
                    // stops exactly on the last bar. Pad with nulls or truncate.
                    if (Array.isArray(globalData) && Array.isArray(labels)) {
                        const desiredLen = labels.length;
                        if (globalData.length > desiredLen) {
                            globalData = globalData.slice(0, desiredLen);
                        } else if (globalData.length < desiredLen) {
                            const padded = new Array(desiredLen).fill(null);
                            for (let i = 0; i < globalData.length; i++) padded[i] = globalData[i];
                            globalData = padded;
                        }
                    }
                    datasets.push({
                        type: 'line',
                        data: globalData,
                        label: this.showBenchmarking ? trad.consumption : trad.globalConsumption,
                        fill: this.showBenchmarking,
                        borderWidth	: 2,
                        borderColor: this.showBenchmarking ? 'rgb(54, 162, 235)' : 'rgb(66, 44, 22)',
                        backgroundColor: this.showBenchmarking ? this.accessibilityEnabled ? pattern.draw('dot-dash', 'rgba(54, 162, 235, 0.3)', undefined, 16) : 'rgba(54, 162, 235, 0.3)' : 'rgba(66, 44, 22, 1)',
                        inputUnit: this.podInputUnit,
                        conversionToKWH: this.conversionToKWH,
                        dataIsInKWH: this.dataIsInKWH,
                        // Marks this dataset for the custom legend marker (line + dot) for Global Consumption
                        _isGlobalConsumptionLine: !this.showBenchmarking
                    })
                    this.showingOverallConsumption = true
                }
                if(this.viewMultiplePods) {
                    this.registersData.forEach(dataByPod => {
                        let generatedDatasets = this.generateDatasetObjects(dataByPod)
                        generatedDatasets.forEach(ds => datasets.push(ds));
                    })
                }
                else {
                    if(this.showBenchmarking) {
                        datasets.push({
                            type: 'line',
                            data: this.benchmarkingCurve,
                            label: trad.averageUserConsumption,
                            fill: true,
                            borderColor: 'rgb(255, 99, 132)',
                            backgroundColor: this.accessibilityEnabled ? pattern.draw('diagonal', 'rgba(255, 99, 132, 0.3)', undefined, 16) : 'rgba(255, 99, 132, 0.3)',
                            inputUnit: this.podInputUnit,
                            conversionToKWH: this.conversionToKWH,
                            dataIsInKWH: this.dataIsInKWH
                        })
                    }
                    else {
                        for(let dsOrder = 0; dsOrder < this.registersData.length; dsOrder++) {
                            let ds = this.registersData[dsOrder];
                            let generatedDatasets = this.generateDatasetObjects(ds);
                            generatedDatasets.forEach(ds => datasets.push(ds));
                        }
                    }
                }
            }

            if (props.aggregation === 'QUARTER_H') {
                const isImpact = props.selectedTimeframes.some(tf => ['EC', 'MD', 'PK'].includes(tf));
                const isBihoraire = props.selectedTimeframes.some(tf => ['HI', 'LO'].includes(tf));

                if (isImpact || isBihoraire) {
                    const timeOfUseConfig = [
                        { start: 0,  end: 1,  impactTariff: 'MD', bihoraireTariff: 'LO' },
                        { start: 1,  end: 7,  impactTariff: 'EC', bihoraireTariff: 'LO' },
                        { start: 7,  end: 11, impactTariff: 'MD', bihoraireTariff: 'HI' },
                        { start: 11, end: 17, impactTariff: 'EC', bihoraireTariff: 'LO' },
                        { start: 17, end: 22, impactTariff: 'PK', bihoraireTariff: 'HI' },
                        { start: 22, end: 24, impactTariff: 'MD', bihoraireTariff: 'LO' }
                    ];

                    let splitDatasets = [];
                    
                    datasets.forEach(ds => {
                        // Only apply colors to PWR timeframe (no PWR_EX)
                        if (ds._timeframe !== 'PWR') {
                            splitDatasets.push(ds);
                            return;
                        }
                        const isInjection = ds.label.toLowerCase().includes('injection') || ds.direction === 'injection' || ds.direction === 1;
                        const dirText = isInjection ? 'Injection' : 'Prélèvement';
                        let buckets = {};
                        
                        // Création des seaux avec les styles automatiques GLOBAUX
                        if (isImpact) {
                            buckets = {
                                'PK': { ...ds, label: `Heures pics - ${dirText}`, ...this.getDatasetStyle('PK', isInjection), data: new Array(ds.data.length).fill(null), _sortOrder: this.getSortOrder('PK', isInjection) },
                                'MD': { ...ds, label: `Heures médiums - ${dirText}`, ...this.getDatasetStyle('MD', isInjection), data: new Array(ds.data.length).fill(null), _sortOrder: this.getSortOrder('MD', isInjection) },
                                'EC': { ...ds, label: `Heures écos - ${dirText}`, ...this.getDatasetStyle('EC', isInjection), data: new Array(ds.data.length).fill(null), _sortOrder: this.getSortOrder('EC', isInjection) }
                            };
                        } else {
                            buckets = {
                                'HI': { ...ds, label: `Heures pleines - ${dirText}`, ...this.getDatasetStyle('HI', isInjection), data: new Array(ds.data.length).fill(null), _sortOrder: this.getSortOrder('HI', isInjection) },
                                'LO': { ...ds, label: `Heures creuses - ${dirText}`, ...this.getDatasetStyle('LO', isInjection), data: new Array(ds.data.length).fill(null), _sortOrder: this.getSortOrder('LO', isInjection) }
                            };
                        }

                        ds.data.forEach((val, i) => {
                            let hour = moment(labels[i]).hour();
                            let currentTOU = timeOfUseConfig.find(tou => hour >= tou.start && hour < tou.end);
                            if (currentTOU) {
                                let targetBucket = isImpact ? currentTOU.impactTariff : currentTOU.bihoraireTariff;
                                if (buckets[targetBucket]) buckets[targetBucket].data[i] = val;
                            }
                        });

                        Object.values(buckets).forEach(b => splitDatasets.push(b));
                    });
                    datasets = splitDatasets;
                }
            }
            datasets.sort((a, b) => (a._sortOrder || 0) - (b._sortOrder || 0));

            let chartDatasets = datasets.map(ds => {
                let cleanData = ds.data;

                if (props.aggregation === 'QUARTER_H') {
                    const offsetDuration = 7.5 * 60 * 1000;
                    cleanData = ds.data.map((val, i) => {
                        const rawValue = (typeof val === 'object' && val !== null) ? val.y : val;
                        
                        // Distinguish between NULL/missing data and actual zeros
                        const isNull = rawValue === null || rawValue === undefined || Number.isNaN(Number(rawValue));
                        const isZero = !isNull && Number(rawValue) === 0;
                        
                        let dataPoint = {
                            t: labels[i] + offsetDuration,
                            y: isNull || isZero ? 0.00001 : rawValue  // Minimal height for hover
                        };
                        
                        // Add markers for tooltip distinction
                        if (isNull) {
                            dataPoint._isNullData = true;
                            dataPoint.backgroundColor = 'rgba(0,0,0,0)';  // Transparent
                        } else if (isZero) {
                            dataPoint._isZeroData = true;
                            dataPoint.backgroundColor = ds.backgroundColor;  // Show color for zero
                        }
                        
                        return dataPoint;
                    });
                }
                return {
                    ...ds,
                    data: cleanData
                };
            });

            if (props.aggregation === 'QUARTER_H' && labels.length > 0) {
                labels.push(labels[labels.length - 1] + 15 * 60 * 1000);
            }
            
            const canvasElement = document.getElementById('chart')
            if(!canvasElement || !this.isMounted) {
                return
            }
            if(this.isCreatingChart) {
                clearTimeout(this.chartTimeout)
            }
            this.isCreatingChart = true
            this.chartTimeout = setTimeout(() => {
                if(!this.isMounted) { return }
                
                if(this.chart) {
                    try {
                        this.chart.stop()
                        this.chart.destroy()
                    } catch(e) {
                        console.error('Error destroying chart:', e)
                    }
                    this.chart = null
                }
                
                const freshCanvasElement = document.getElementById('chart')
                if(!freshCanvasElement || !this.isMounted) {
                    this.isCreatingChart = false
                    return
                }
                
                Chart.defaults.global.defaultFontFamily = "Segoe UI";
                this.chart = new Chart(freshCanvasElement.getContext('2d'), {
                    plugins: [globalConsumptionLegendMarkerPlugin],
                    type: this.showBenchmarking ? 'line' : 'bar',
                    data: {
                        labels: labels,
                        datasets: chartDatasets
                    },
                    options: options
                })
                this.isCreatingChart = false
            }, 50)
        },
        generateXAxisLabels() {
            moment.locale(this.userLanguage.toLowerCase())
            let labels = [];
            if(this.compareByYear) {
                let year = this.hasLeapYear ? 2020 : 2019;
                let startDate = moment(`${year}-${this.fromMonth}-${this.fromDay}`, "YYYY-MM-DD");
                let endDate = moment(`${year}-${this.toMonth}-${this.toDay}`, "YYYY-MM-DD");
                let date;
                switch(this.aggregation) {
                    case "QUARTER_H":
                        date = moment(new Date(moment(startDate).format("YYYY-MM-DD")));
                        while(date < endDate) {
                            labels.push(date.valueOf());
                            date = date.add(15, "minutes");
                        }
                        break;
                    case "HOUR":
                        date = moment(new Date(moment(startDate).format("YYYY-MM-DD")));
                        while(date < endDate) {
                            labels.push(date.format("DD-MM, HH:mm"));
                            date = date.add(1,"hour");
                        }
                        break;
                    case "DAY":
                        date = moment(startDate);
                        while(date < endDate) {
                            labels.push(date.format("DD-MM"));
                            date = date.add(1, 'day');
                        }
                        break;
                    case "WEEK":
                        date = moment(startDate);
                        while(date < endDate) {
                            labels.push("Semaine " + date.format('w'));
                            date = date.add(1, 'week');
                        }
                        break;
                    case "MONTH":
                        date = moment(startDate);
                        while(date < endDate) {
                            labels.push(date.format("MM"));
                            date = date.add(1, 'month');
                        }
                        break;
                    case "YEAR":
                        labels[0] = "Année";
                        break;
                }
            }
            else {
                switch(this.aggregation) {
                    case "QUARTER_H":
                        for(let i = 0; i < this.allDates.length - 1; i++) {
                            labels.push(moment(this.allDates[i]).valueOf());
                        }
                        break;
                    case "HOUR":
                        for(let i = 0; i < this.allDates.length - 1; i++) {
                            labels.push(moment(this.allDates[i]).format("DD-MM, HH:mm"));
                        }
                        break;
                    case "DAY":
                        for(let i = 0; i < this.allDates.length - 1; i++) {
                            labels.push(moment(this.allDates[i]).format('DD-MM-YY'));
                        }
                        break;
                    case "WEEK":
                        for(let i = 0; i < this.allDates.length - 1; i++) {
                            labels.push(moment(this.allDates[i]).format(`${moment(this.allDates[i]).format('w')}-YYYY`))
                        }
                        break;
                    case "MONTH":
                        for(let i = 0; i < this.allDates.length - 1; i++) {
                            labels.push(moment(this.allDates[i]).format('MM-YYYY'));
                        }
                        break;
                    case "YEAR":
                        for(let i = 0; i < this.allDates.length - 1; i++) {
                            labels.push(new Date(this.allDates[i]).getFullYear());
                        }
                        break;
                }
                // mark the incomplete periods
                /*
                let periodsNotedAsIncomplete = []
                this.groupedIncompletePeriods.forEach(groupedIP => {
                    groupedIP.incompletePI.forEach(index => {
                        if(!periodsNotedAsIncomplete.includes(index)) {
                            labels[index] = String(labels[index]).concat(" (*)")
                            periodsNotedAsIncomplete.push(index)
                        }
                    })
                })*/
               // mark the incomplete periods
                let periodsNotedAsIncomplete = []
                this.groupedIncompletePeriods.forEach(groupedIP => {
                    groupedIP.incompletePI.forEach(index => {
                        if(!periodsNotedAsIncomplete.includes(index)) {
                            if (this.aggregation !== 'QUARTER_H') {
                                labels[index] = String(labels[index]).concat(" (*)")
                            }
                            periodsNotedAsIncomplete.push(index)
                        }
                    })
                })
            }
            return labels
        },
        // year only sent if compareByYear is true
        // index et podID seulement necessaires si viewMultiplePods == true
        /*
        generateDatasetObjects(ds, registerID, yearI, year, index) {
            let registerDs = []
            let colorOpacityIndex
            switch(ds.timeframe) {
                case 'TH':
                    colorOpacityIndex = 2
                    break
                case 'HI':
                    colorOpacityIndex = 3
                    break
                case 'LO':
                    colorOpacityIndex = 4
                    break
                default:
                    colorOpacityIndex = 2
            }

            if(this.viewMultiplePods) {
                // if view multiple pods is true then the ds objects contains registersData grouped by POD
                let label = ds.podCustomName
                registerDs.push({
                    label: label,
                    backgroundColor: this.colors[index % this.colors.length][2],
                    borderColor: this.colors[index % this.colors.length][0],
                    borderWidth: 2,
                    data: ds.overallConsumptions,
                    inputUnit: "kWh",
                    conversionToKWH: 1,
                    dataIsInKWH: true,
                })
                if(this.comparePods) {
                    registerDs.forEach(rds => rds.stack = ds.pod.id);
                }
                return registerDs
            }
            else {
                if(this.compareByYear) {
                    let label = this.label.forms.year + " " + year
                    registerDs.push({
                        label: label,
                        backgroundColor: this.colors[yearI][2],
                        borderColor: this.colors[yearI][0],
                        borderWidth: 2,
                        data: ds,
                        inputUnit: this.podInputUnit,
                        conversionToKWH: this.conversionToKWH,
                        dataIsInKWH: this.dataIsInKWH,
                        stack: yearI
                    })
                } else {
                    // let label = this.label.texts.register + " " + ds.variableName
                    // let label = "" + getSCEFriendlyName(ds.variableName) + " - " + ds.timeframe
                    let label = "" + getRegisterFriendlyName(ds.variableName)
                    // if(this.compareByYear)
                    //         label += "(" + year + ")";
                    registerDs.push({
                        label: label,
                        backgroundColor: this.registersColors[registerID][colorOpacityIndex],
                        borderColor: this.registersColors[registerID][0],
                        borderWidth: 2,
                        data: ds.consumptions,
                        costPerPeriod: ds.costPerPeriod,
                        inputUnit: this.podInputUnit,
                        conversionToKWH: this.conversionToKWH,
                        dataIsInKWH: this.dataIsInKWH,
                    })
                    // if(year) {
                    //     registerDs.forEach(ds => ds.stack = year);
                    // }
                }
            }
            return registerDs
        },*/
        generateDatasetObjects(ds, yearI, year) {
            let registerDs = [];
            const timeframe = ds.timeframe || 'TH';
            const friendlyName = ds.variableName ? getRegisterFriendlyName(ds.variableName) : '';
            let labelForStyle = this.compareByYear
                ? `${this.label.forms.year} ${year}`
                : (this.viewMultiplePods ? ds.podCustomName : (friendlyName || ds.variableName || ''));

            const directionHint = [ds.direction, ds.variableName, labelForStyle]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            const isInjection = directionHint.includes('injection') || directionHint.includes('inject');

            if (!this.compareByYear && !this.viewMultiplePods) {
                const directionLabel = isInjection ? 'Injection' : 'Prélèvement';
                if (timeframe === 'PWR') {
                    labelForStyle = `Mono-horaire - ${directionLabel}`;
                } else if (timeframe === 'PWR_EX' || timeframe === 'EX') {
                    labelForStyle = `Exclusif nuit - ${directionLabel}`;
                }
            }
            
            const style = this.getDatasetStyle(timeframe, isInjection);
            const sortOrder = this.getSortOrder(timeframe, isInjection);

            if(this.viewMultiplePods) {
                registerDs.push({
                    label: ds.podCustomName,
                    ...style, // Applique les couleurs
                    data: ds.overallConsumptions,
                    inputUnit: "kWh",
                    conversionToKWH: 1,
                    dataIsInKWH: true,
                    _sortOrder: sortOrder,
                    _timeframe: timeframe
                })
                if(this.comparePods) registerDs.forEach(rds => rds.stack = ds.pod.id);
            }
            else if(this.compareByYear) {
                registerDs.push({
                    label: `${this.label.forms.year} ${year}`,
                    ...style,
                    data: ds,
                    inputUnit: this.podInputUnit,
                    conversionToKWH: this.conversionToKWH,
                    dataIsInKWH: this.dataIsInKWH,
                    stack: yearI,
                    _sortOrder: sortOrder,
                    _timeframe: timeframe
                })
            } else {
                registerDs.push({
                    label: labelForStyle,
                    ...style,
                    data: ds.consumptions,
                    costPerPeriod: ds.costPerPeriod,
                    inputUnit: this.podInputUnit,
                    conversionToKWH: this.conversionToKWH,
                    dataIsInKWH: this.dataIsInKWH,
                    _sortOrder: sortOrder,
                    _timeframe: timeframe
                })
            }
            return registerDs;
        },
        handleBarClick(datasetIndex, periodIndex) {
            this.$emit('clickedBar', datasetIndex, periodIndex)
        },
        // --- NOUVELLES FONCTIONS DE STYLE ET DE TRI ---
        getDatasetStyle(timeframe, isInjection) {
            // Couleurs de base pour le PRÉLÈVEMENT
            const baseColors = {
                'PK': { r: 255, g: 99,  b: 132 }, // Rouge/Rose
                'MD': { r: 54,  g: 162, b: 235 }, // Bleu
                'EC': { r: 103, g: 212, b: 118 }, // Vert
                'HI': { r: 255, g: 159, b: 64 },  // Orange
                'LO': { r: 75,  g: 192, b: 192 }, // Turquoise
                'PWR': { r: 34,  g: 83,  b: 148 },
                'TH': { r: 34,  g: 83,  b: 148 }, // Bleu foncé (Global)
                'EX': { r: 153, g: 102, b: 255 }, // Violet (Exclusif Nuit)
                'PWR_EX': { r: 153, g: 102, b: 255 } // Violet (Exclusif Nuit, vue 15')
            };

            let c = baseColors[timeframe] || baseColors['TH'];

            // Si c'est de l'injection, on inverse mathématiquement les couleurs sur le spectre RGB (255 - valeur)
            if (isInjection) {
                c = { r: 255 - c.r, g: 255 - c.g, b: 255 - c.b };
            }

            let bgColor = `rgba(${c.r}, ${c.g}, ${c.b}, 0.8)`;
            
            // Accessibilité (Textures au lieu de couleurs unies)
            if (this.accessibilityEnabled) {
                const patterns = { 'TH': 'dash', 'PWR': 'dash', 'EX': 'triangle', 'PWR_EX': 'triangle', 'PK': 'cross', 'MD': 'dot', 'EC': 'line', 'HI': 'ring', 'LO': 'plus' };
                bgColor = pattern.draw(patterns[timeframe] || 'circle', `rgba(${c.r}, ${c.g}, ${c.b}, 0.8)`, undefined, 16);
            }

            return {
                backgroundColor: bgColor,
                borderColor: `rgb(${c.r}, ${c.g}, ${c.b})`,
                borderWidth: 2
            };
        },
        getSortOrder(timeframe, isInjection) {
            // Ordre : TH/PWR=0, PK=1, HI=1, MD=2, LO=2, EC=3
            const orderMap = { 'TH': 0, 'PWR': 0, 'EX': 0, 'PWR_EX': 0, 'PK': 1, 'HI': 1, 'MD': 2, 'LO': 2, 'EC': 3 };
            const baseOrder = orderMap[timeframe] !== undefined ? orderMap[timeframe] : 9;
            // +10 pour l'injection pour forcer l'affichage à la fin
            return baseOrder + (isInjection ? 10 : 0);
        }
    },
    beforeDestroy() {
        this.isMounted = false;
        if(this.chartTimeout) {
            clearTimeout(this.chartTimeout)
            this.chartTimeout = null
        }
        if(this.chart) {
            try {
                this.chart.stop()
                this.chart.destroy()
            } catch(e) {
                console.error('Error destroying chart in beforeDestroy:', e)
            }
            this.chart = null
        }
    },
    watch: {
        registersData() {
            this.displayChart()
         },
        userLanguage() {
            this.displayChart()
        },
        comparePods() {
            this.displayChart()
        }
    }
}
</script>

<style scoped>
.chart-container {
  min-height: 400px;
}

@media screen and (max-width: 769px) {
  .chart-container {
    max-width: 100%;
  }
}

@media screen and (min-width: 769px) {
  .chart-container {
    max-width: 60vw;
  }
}

@media screen and (min-width: 1024px) {
  .chart-container {
    max-width: 592px;
  }
}

@media screen and (min-width: 1216px) {
  .chart-container {
    max-width: 720px;
  }
}

@media screen and (min-width: 1408px) {
  .chart-container {
    max-width: 848px;
  }
}
</style>